import React, { useState, useEffect } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Trash, X } from '@phosphor-icons/react';

export default function PropertiesPanel({ selectedNode, selectedEdge, onUpdateNode, onUpdateEdge, onDeleteNode, onDeleteEdge, onClose }) {
  const [nodeData, setNodeData] = useState({});
  const [edgeData, setEdgeData] = useState({});

  useEffect(() => {
    if (selectedNode) {
      setNodeData({ ...(selectedNode.data || {}) });
    }
  }, [selectedNode]);

  useEffect(() => {
    if (selectedEdge) {
      setEdgeData({ ...(selectedEdge.data || {}) });
    }
  }, [selectedEdge]);

  const handleNodeSave = () => {
    if (selectedNode) onUpdateNode(selectedNode.id, nodeData);
  };

  const handleEdgeSave = () => {
    if (selectedEdge) onUpdateEdge(selectedEdge.id, edgeData);
  };

  if (selectedNode) {
    const isGateway = ['exclusive', 'parallel', 'sync', 'merge'].includes(selectedNode.type);
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-zinc-100">Node Properties</p>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-300"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-1">
          <span className="text-[10px] uppercase tracking-wider text-zinc-500">{selectedNode.type}</span>
          <p className="text-xs font-mono text-zinc-600">{selectedNode.id}</p>
        </div>
        <div className="space-y-2">
          <Label className="text-zinc-400 text-xs">Label</Label>
          <Input value={nodeData.label || ''} onChange={e => setNodeData({ ...nodeData, label: e.target.value })}
            className="bg-zinc-800 border-zinc-700 text-zinc-100 h-8 text-xs" />
        </div>
        {selectedNode.type === 'task' && (
          <>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-xs">Description</Label>
              <Input value={nodeData.description || ''} onChange={e => setNodeData({ ...nodeData, description: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-zinc-100 h-8 text-xs" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-xs">Status Value</Label>
              <Input value={nodeData.status_value || ''} onChange={e => setNodeData({ ...nodeData, status_value: e.target.value })}
                placeholder="e.g. inspected, packed" className="bg-zinc-800 border-zinc-700 text-zinc-100 h-8 text-xs" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-xs">Assignee Role</Label>
              <Input value={nodeData.assignee_role || ''} onChange={e => setNodeData({ ...nodeData, assignee_role: e.target.value })}
                placeholder="e.g. inspector, manager" className="bg-zinc-800 border-zinc-700 text-zinc-100 h-8 text-xs" />
            </div>
          </>
        )}
        <div className="flex gap-2 pt-2">
          <Button size="sm" onClick={handleNodeSave} className="bg-brand-600 hover:bg-brand-700 text-white text-xs flex-1" data-testid="save-node-btn">
            Save
          </Button>
          {!['start', 'end'].includes(selectedNode.type) && (
            <Button size="sm" variant="ghost" onClick={() => onDeleteNode(selectedNode.id)} className="text-red-500 hover:bg-red-500/10" data-testid="delete-node-btn">
              <Trash className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (selectedEdge) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-zinc-100">Edge Properties</p>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-300"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-1">
          <p className="text-xs font-mono text-zinc-600">{selectedEdge.source} &rarr; {selectedEdge.target}</p>
        </div>
        <div className="space-y-2">
          <Label className="text-zinc-400 text-xs">Label</Label>
          <Input value={edgeData.label || ''} onChange={e => setEdgeData({ ...edgeData, label: e.target.value })}
            placeholder="e.g. Pass, Fail" className="bg-zinc-800 border-zinc-700 text-zinc-100 h-8 text-xs" />
        </div>
        <div className="space-y-2">
          <Label className="text-zinc-400 text-xs">Condition Expression</Label>
          <Input value={edgeData.condition || ''} onChange={e => setEdgeData({ ...edgeData, condition: e.target.value })}
            placeholder="e.g. qc_result == 'pass'" className="bg-zinc-800 border-zinc-700 text-zinc-100 h-8 text-xs font-mono" />
          <p className="text-[10px] text-zinc-600">Operators: ==, !=, &gt;, &lt;, in [a,b], contains</p>
        </div>
        <div className="flex items-center gap-2">
          <input type="checkbox" checked={edgeData.is_default || false}
            onChange={e => setEdgeData({ ...edgeData, is_default: e.target.checked })}
            className="rounded border-zinc-600" />
          <Label className="text-zinc-400 text-xs">Default path (fallback)</Label>
        </div>
        <div className="flex gap-2 pt-2">
          <Button size="sm" onClick={handleEdgeSave} className="bg-brand-600 hover:bg-brand-700 text-white text-xs flex-1" data-testid="save-edge-btn">
            Save
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onDeleteEdge(selectedEdge.id)} className="text-red-500 hover:bg-red-500/10" data-testid="delete-edge-btn">
            <Trash className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="text-center py-8">
      <p className="text-xs text-zinc-600">Select a node or edge to edit properties</p>
    </div>
  );
}
